import { useCallback, useEffect, useMemo, useState } from 'react';

import InfoSection from '../../../../global/InfoSection/InfoSection';
import crud from '../../../../../../api/crud';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../../../global/Button';
import DotsLoader from '../../../../global/DotsLoader';
import InputPin from '../../../../global/InputPin';
import {
  getNextButtonStyles,
  getOptionButtonStyles,
  isTechRecyclingProductType,
  isVirtualBoxRecyclingProductType,
} from '../../../../utils/tbbRegistration';

import './Pin.scss';
import PinModal from './PinModal';
import PaymentSuccess from '../../Components/Animations/PaymentSuccess';

const Pin = ({
  storeConfig,
  setPinValues,
  pinValues,
  registerCode,
  onSubmit,
  onCancel,
  onShowingAnimation,
  recyclingProductType,
  stepTrackerProps,
  isPaymentSuccessful,
  isBlocked,
}) => {
  const [isValidPin, setIsValidPin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(isBlocked ? 0 : 5);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(isPaymentSuccessful);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const isTechRecycling = isTechRecyclingProductType(recyclingProductType);
  const isVirtualBox = isVirtualBoxRecyclingProductType(recyclingProductType);

  const {
    registerTbbFlow,
    isTrashie,
  } = useMemo(() => storeConfig, [storeConfig]);

  const descriptionText = isTrashie ? 'Psst, you can find it on the' :
    registerTbbFlow?.enterPinStep?.descriptionText;

  const handlePinCode = useCallback(async () => {
    setLoading(true);

    const response = await crud.post({
      path: '/redemptions/validatePinCode',
      body: {
        data: {
          id: registerCode,
          pin: pinValues.join(''),
        },
      },
    });

    if (response.invalid) {
      setIsValidPin(false);
      setAttempts(response.attempts);
    } else {
      onSubmit();
    }

    setLoading(false);
  }, [pinValues, registerCode, onSubmit]);

  const toggleDialog = () => setIsOpenDialog((isOpen) => !isOpen);

  const onHandlePinChange = useCallback((pin) => {
    setIsValidPin(true);
    setPinValues(pin);
  }, [setPinValues]);

  useEffect(() => {
    let timerId;
    if (isPaymentSuccessful) {
      onShowingAnimation?.(true);

      timerId = setTimeout(() => {
        setShowPaymentSuccess(false);
        onShowingAnimation?.(false);
      }, 2500);
    }

    return () => clearTimeout(timerId);
  }, [isPaymentSuccessful]);

  if (showPaymentSuccess) {
    return <PaymentSuccess isPayOnReg />;
  }

  const isBlockedOrZeroAttempts = attempts === 0 || isBlocked;

  return (
    <div className="RegisterPin">
      <InfoSection
        titleText={`Enter your ${(isTechRecycling || isVirtualBox) ? 'box' : 'bag'} pin`}
        descriptionText={descriptionText}
        descriptionLinkText={isTrashie ? `lower right corner of your ${(isTechRecycling || isVirtualBox) ? 'box' : 'bag'}` : null}
        descriptionLinkClick={isTrashie ? toggleDialog : null}
      />
      <InputPin
        className="RegisterPin__pin"
        isValidPin={isValidPin}
        pinValues={pinValues}
        setPinValues={onHandlePinChange}
        errorMessage={
          isBlocked ? 'You have tried to redeem the code more than 5 times with a wrong PIN, please contact support.' :
            `Invalid Pin. You have ${attempts} ${registerTbbFlow?.enterPinStep?.wrongPinText || 'more attempts.'}
            ${attempts === 0 ? ' Please contact support to get help.' : ''}`
        }
        isBlocked={isBlocked}
      />
      <Button
        type={BUTTON_TYPE.QUATERNARY}
        size={BUTTON_SIZE.LARGE}
        onClick={isBlockedOrZeroAttempts ? onCancel : handlePinCode}
        style={getNextButtonStyles(storeConfig)}
        disabled={attempts !== 0 && (pinValues.join('').length < 6 || loading)}
        trackerProps={stepTrackerProps}
      >
        {loading ? (
          <DotsLoader />
        ) : (
          (isBlockedOrZeroAttempts && 'CONTACT SUPPORT') ||
          'CONTINUE'
        )}
      </Button>
      {attempts !== 0 && (
        <div className="RegisterPin__option">
          <p className="RegisterPin__option--text">
            PIN code not working?
          </p>
          <Button
            type={BUTTON_TYPE.LINK_QUINARY}
            onClick={onCancel}
            style={getOptionButtonStyles(storeConfig)}
          >
            CONTACT US FOR HELP
          </Button>
        </div>
      )}
      <PinModal
        isOpen={isOpenDialog}
        onClose={toggleDialog}
        isTechRecycling={isTechRecycling || isVirtualBox}
      />
    </div>
  );
};

export default Pin;
